import React, {useEffect, useState} from 'react';
import {Link} from 'react-router-dom';
import Menu from '../Menu';
import PageHeader from '../PageHeader';
import LessonCard from './LessonCard';
import {LearningOverview, LearningLesson, learningCount, learningLessonUrl, loadLearningOverview} from './learning';
import './Learning.scss';
import {isMissingStaffProfile} from '../../staffAccess';

type LearningTab = 'categories' | 'in_progress' | 'passed';

export function KnowledgeCategories() {
    const [overview, setOverview] = useState<LearningOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [profileMissing, setProfileMissing] = useState(false);
    const [tab, setTab] = useState<LearningTab>('categories');
    const [search, setSearch] = useState('');
    const [reload, setReload] = useState(0);

    useEffect(() => {
        let active = true;
        setLoading(true);
        setError('');
        setProfileMissing(false);
        loadLearningOverview().then(data => {
            if (!active) return;
            setOverview(data);
            setLoading(false);
        }).catch(error => {
            if (!active) return;
            const missing = isMissingStaffProfile(error);
            setProfileMissing(missing);
            setError(missing
                ? 'Для этого аккаунта пока не подключён профиль мастера. Если вы работаете в салоне, попросите руководителя проверить ваш номер телефона в CRM и доступ к приложению Staff.'
                : 'Не удалось загрузить обучение. Попробуйте ещё раз.');
            setLoading(false);
        });
        return () => { active = false; };
    }, [reload]);

    const allLessons = (overview?.categories || []).reduce((items, category) => items.concat(category.lessons), [] as LearningLesson[]);
    const query = search.trim().toLocaleLowerCase('ru-RU');
    const filteredLessons = query
        ? allLessons.filter(lesson => lesson.title.toLocaleLowerCase('ru-RU').includes(query))
        : tab === 'passed'
            ? allLessons.filter(lesson => lesson.status === 'passed')
            : allLessons.filter(lesson => ['opened', 'in_progress', 'retry'].includes(lesson.status));
    const nextLesson = overview?.continueLesson;
    const progress = overview?.progress;

    return <div className='main learning-page'>
        <div className='content mx-w-content'>
            <PageHeader pageTitle='Обучение' backBtnUrl='/'/>
            <div className='pt-header'/>
            <div className='learning-container'>
                <div className='learning-intro'>
                    <h2>Учитесь в удобном темпе</h2>
                    <p>Уроки и короткие тесты помогут закрепить навыки. За первый успешно пройденный тест начисляются ЧиоКоины.</p>
                </div>

                {loading && <div className='learning-state' role='status'>Загружаем уроки…</div>}
                {error && <div className={`learning-state ${profileMissing ? 'learning-access-state' : 'is-error'}`} role='alert'>
                    {profileMissing && <h3>Нужен профиль мастера</h3>}
                    <p>{error}</p>
                    {profileMissing ? <Link to='/'>На главную</Link>
                        : <button type='button' onClick={() => setReload(value => value + 1)}>Повторить</button>}
                </div>}

                {!loading && !error && overview && <>
                    {nextLesson && <Link to={learningLessonUrl(nextLesson)} className='learning-continue'>
                        <span className='learning-continue-label'>{nextLesson.status === 'new' ? 'С чего начать' : 'Продолжить обучение'}</span>
                        <strong>{nextLesson.title}</strong>
                        <span className='learning-continue-bottom'>{nextLesson.duration || 'Урок'}<b>Открыть <span aria-hidden='true'>→</span></b></span>
                    </Link>}

                    {progress && progress.totalTests > 0 && <div className='learning-progress-card'>
                        <div><strong>{progress.passedTests} из {progress.totalTests}</strong><span>тестов пройдено</span></div>
                        <div className='learning-progress-track' role='progressbar' aria-valuenow={progress.passedTests} aria-valuemin={0} aria-valuemax={progress.totalTests} aria-label='Пройдено тестов'>
                            <span style={{width: `${Math.round(progress.passedTests / progress.totalTests * 100)}%`}}/>
                        </div>
                    </div>}

                    <label className='learning-search'>
                        <svg aria-hidden='true' width='19' height='19' viewBox='0 0 24 24' fill='none'><circle cx='10.7' cy='10.7' r='6.7' stroke='currentColor' strokeWidth='1.8'/><path d='m16 16 5 5' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round'/></svg>
                        <input type='search' value={search} onChange={event => setSearch(event.target.value)} placeholder='Найти урок' aria-label='Найти урок'/>
                    </label>

                    {!query && <div className='learning-tabs' role='group' aria-label='Разделы обучения'>
                        <button type='button' aria-pressed={tab === 'categories'} className={tab === 'categories' ? 'is-active' : ''} onClick={() => setTab('categories')}>Категории</button>
                        <button type='button' aria-pressed={tab === 'in_progress'} className={tab === 'in_progress' ? 'is-active' : ''} onClick={() => setTab('in_progress')}>В процессе</button>
                        <button type='button' aria-pressed={tab === 'passed'} className={tab === 'passed' ? 'is-active' : ''} onClick={() => setTab('passed')}>Пройдено</button>
                    </div>}

                    {query || tab !== 'categories' ? <section className='learning-section'>
                        <h3>{query ? 'Результаты поиска' : tab === 'passed' ? 'Пройденные тесты' : 'Продолжить обучение'}</h3>
                        {filteredLessons.length > 0 ? filteredLessons.map(lesson => <LessonCard key={lesson.id} lesson={lesson}/>)
                            : <div className='learning-empty'>{query ? 'Уроков по этому запросу не найдено.' : tab === 'passed' ? 'Пройденные тесты появятся здесь.' : 'Начните с любого урока в категориях.'}</div>}
                    </section> : <section className='learning-section'>
                        <h3>Категории</h3>
                        {overview.categories.length > 0 ? overview.categories.map((category, index) =>
                            <Link to={`/knowledge-base/${category.id}`} className='learning-category' key={category.id}>
                                <span className={`learning-category-icon tone-${index % 4}`} aria-hidden='true'>{String(index + 1).padStart(2, '0')}</span>
                                <span className='learning-category-copy'><strong>{category.title}</strong><small>{learningCount(category.lessonCount, 'урок', 'урока', 'уроков')}{category.testCount > 0 ? ` · ${category.passedCount} из ${category.testCount} тестов пройдено` : ''}</small></span>
                                <span className='learning-card-arrow' aria-hidden='true'>›</span>
                            </Link>
                        ) : <div className='learning-empty'>Уроки пока не опубликованы.</div>}
                    </section>}
                </>}
            </div>
        </div>
        <Menu/>
    </div>;
}

export default KnowledgeCategories;
